import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Box, Typography, Paper, Tabs, Tab, Alert, LinearProgress, Container } from '@mui/material';
import { Storefront, Map, AccessTime, Description, PhotoSizeSelectActual, AccountBalance, ErrorOutline, VolumeUp } from '@mui/icons-material';

// Sub-components
import VendorLocation from './VendorLocation';
import VendorAvailability from './VendorAvailability';
import VendorBio from './VendorBio';
import VendorBusiness from './VendorBusiness';
import VendorBanner from './VendorBanner';
import BankAccountManager from 'views/common/bank-accounts/BankAccountManager';
import VendorAlertSoundSettings from './VendorAlertSoundSettings';
import { useCurrentVendor } from 'hooks/useUser';

const VendorSettings = () => {
    const { data, isLoading } = useCurrentVendor();
    const { hash } = useLocation();
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        if (hash === '#map') {
            setActiveTab(4);
        }
    }, [hash]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const TabPanel = ({ children, value, index }) => (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ py: { xs: 2, md: 4 } }}>{children}</Box>}
        </div>
    );

    TabPanel.propTypes = {
        children: PropTypes.node,
        value: PropTypes.number.isRequired,
        index: PropTypes.number.isRequired
    };

    if (isLoading) return <LinearProgress />;

    return (
        <Container>
            <Box mb={4}>
                <Typography variant="h4" fontWeight={900} gutterBottom>
                    Store Settings
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage your branding, business hours, and payment preferences.
                </Typography>
            </Box>

            <Paper elevation={0} sx={{ border: '1px solid #eee', borderRadius: 4, overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        px: 2,
                        bgcolor: '#fafafa',
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': { py: 2, minHeight: 64, fontWeight: 700, textTransform: 'none' }
                    }}
                >
                    <Tab icon={<PhotoSizeSelectActual />} iconPosition="start" label="Branding" />
                    <Tab icon={<Storefront />} iconPosition="start" label="Business Info" />
                    <Tab icon={<Description />} iconPosition="start" label="Bio" />
                    <Tab icon={<AccessTime />} iconPosition="start" label="Availability" />
                    <Tab icon={<Map />} iconPosition="start" label="Location" />
                    <Tab icon={<AccountBalance />} iconPosition="start" label="Payouts" />
                    <Tab icon={<VolumeUp />} iconPosition="start" label="Alert Sound" />
                </Tabs>

                <Box sx={{ p: { xs: 2, md: 0 } }}>
                    {/* SYSTEM WARNINGS SECTION */}
                    {!data?.vendor?.location && (
                        <Alert severity="warning" icon={<ErrorOutline />} sx={{ m: 3, borderRadius: 2 }}>
                            Your store location is not set. Customers won't be able to find you!
                        </Alert>
                    )}

                    {/* TAB CONTENT AREAS */}

                    {/* 0. Branding (Banner & Profile) */}
                    <TabPanel value={activeTab} index={0}>
                        <VendorBanner data={data} />
                    </TabPanel>

                    {/* 1. Business Info (Name, Email, etc) */}
                    <TabPanel value={activeTab} index={1}>
                        <VendorBusiness data={data} />
                    </TabPanel>

                    {/* 2. Bio (Description) */}
                    <TabPanel value={activeTab} index={2}>
                        <VendorBio data={data} />
                    </TabPanel>

                    {/* 3. Availability (Opening/Closing Hours) */}
                    <TabPanel value={activeTab} index={3}>
                        <VendorAvailability data={data} />
                    </TabPanel>

                    {/* 4. Location (Address & Map Selection) */}
                    <TabPanel value={activeTab} index={4}>
                        <VendorLocation data={data} />
                    </TabPanel>

                    {/* 5. Payouts (Bank Details) */}
                    <TabPanel value={activeTab} index={5}>
                        <BankAccountManager />
                    </TabPanel>

                    {/* 6. Alert Sound Settings */}
                    <TabPanel value={activeTab} index={6}>
                        <VendorAlertSoundSettings />
                    </TabPanel>
                </Box>
            </Paper>
        </Container>
    );
};

export default VendorSettings;
